import { Inject, Injectable } from '@angular/core';
import { AmplifyService } from 'aws-amplify-angular';
import { AmplifyUser, UserProfile } from './models/user-profile.model';
import { DefaultAuthHeaders } from './models/default-auth-headers.model';
import { Hub } from '@aws-amplify/core';
import { Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthApiAbstract } from './auth-api-abstract';
import { ENVIRONMENT } from '../models/environment.token';
import { EnvironmentInterface } from '../models/environment.interface';
// import { RememberLocationService } from '../services/remember-location.service';
import { OpenLinkWithReferrerService } from './open-link-with-referrer.service';
import { Config } from '../models/config.interface';
import Amplify from '@aws-amplify/core';

@Injectable({
  providedIn: 'root'
})
export class AuthApiService extends AuthApiAbstract {
  private config: Config = {} as Config;

  public constructor(private openLinkWithReferrerService: OpenLinkWithReferrerService, @Inject(ENVIRONMENT) private environment: EnvironmentInterface, private amplifyService: AmplifyService, private http: HttpClient) {
    super();
  }

  public async init(config: Config) {
    this.config = config;
    const oauth = {
      clientId: this.config.idp.clientId,
      domain: this.config.idp.userPoolDomain,
      scope: ['email', 'openid', 'profile'],
      redirectSignIn: `${window.location.origin}/login`,
      redirectSignOut: `${window.location.origin}/logout`,
      responseType: 'code',
    };

    const amplifyConfig = {
      aws_project_region: 'eu-central-1',
      aws_cognito_region: 'eu-central-1',
      aws_user_pools_id: this.config.idp.userPoolId,
      aws_user_pools_web_client_id: this.config.idp.clientId,
      oauth,
    };

    Amplify.configure(amplifyConfig);
  }

  /**
   * This is only used when coming from external login screen due to an Amplify issue.
   */
  public listenForAuthAfterExternalSignin(): Observable<boolean> {
    const signinSubject = new Subject<boolean>();
    Hub.listen('auth', async ({ payload: { event, data } }) => {
      if (event === 'signIn') {
        signinSubject.next(true);
      }
    });
    return signinSubject.asObservable();
  }

  public async getCurrentUser(): Promise<UserProfile | null> {
    try {
      const amplifyUser = await this.amplifyService.auth().currentAuthenticatedUser();
      return this.formatUserProfile(amplifyUser);
    } catch (e) {
      return null;
    }
  }

  public async getJwt(): Promise<string> {
    const currentUser = await this.amplifyService.auth().currentAuthenticatedUser();

    return currentUser
      .getSignInUserSession()
      .getIdToken()
      .getJwtToken();
  }

  public async signOut(): Promise<void> {
    try {
      const jwt = await this.getJwt();
      await this.http
        .get(`${this.environment.apiUrl}/hub-logout`, {
          headers: { authorization: jwt }
        })
        .toPromise();
      await this.amplifyService.auth().signOut();
    } catch (e) {
      // console.log(e);
      await this.amplifyService.auth().signOut();
      return;
    }
  }

  public async getDefaultAuthHeaders(): Promise<DefaultAuthHeaders> {
    const jwtToken = await this.getJwt();
    return {
      'Cognito-Authorization': jwtToken
    };
  }

  public loginWithSaml(): void {
    const url = `https://${this.config.idp.userPoolDomain}/oauth2/authorize?&redirect_uri=${window.location.origin}/login&response_type=code&client_id=${this.config.idp.clientId}&scopes=phone%2Cemail%2Copenid%2Cprofile`;
    this.openLinkWithReferrerService.openLink(url);
  }

  private formatUserProfile(amplifyUser: AmplifyUser): UserProfile {
    const signInUser: any = amplifyUser.signInUserSession;
    const signInUserPayload = signInUser.idToken.payload;
    return {
      username: amplifyUser.username,
      sub: signInUserPayload.sub,
      name: `${signInUserPayload.given_name} ${signInUserPayload.family_name ? signInUserPayload.family_name : ''}`,
      givenName: signInUserPayload.given_name,
      familyName: signInUserPayload.family_name,
      email: signInUserPayload.email,
      jwtId: signInUser.idToken.jwtToken,
      jwtAccess: signInUser.accessToken.jwtToken
    } as UserProfile;
  }
}
